import { useState, useEffect, useRef, useCallback } from "react";
import { LienEntiteForm, type LienEntiteFormData } from "@/components/ContactLiens";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { authFetch } from "@/lib/authFetch";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { X, Search, Plus, User, Phone } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface ContactTelephone {
  libelle: string;
  numero: string;
  principal: boolean;
}

export interface Contact {
  id: string;
  prenom: string;
  nom: string;
  poste?: string | null;
  avatarUrl?: string | null;
  email?: string | null;
  telephones?: ContactTelephone[];
  organisationType?: string | null;
  organisationId?: string | null;
  organisationLibre?: string | null;
  organisationNom?: string | null;
  notes?: string | null;
  source?: string;
}

interface ContactSelectorProps {
  value: string[];
  onChange: (ids: string[]) => void;
  multiple?: boolean;
  organisationFilter?: string | null;
  allowCreate?: boolean;
  placeholder?: string;
  disabled?: boolean;
}

function avatarInitials(prenom: string, nom: string): string {
  const a = (prenom?.[0] || "").toUpperCase();
  const b = (nom?.[0] || "").toUpperCase();
  return (a + b) || "?";
}

function avatarColor(name: string): string {
  const colors = [
    "bg-blue-500", "bg-emerald-500", "bg-violet-500", "bg-orange-500",
    "bg-pink-500", "bg-teal-500", "bg-indigo-500", "bg-amber-500",
  ];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return colors[Math.abs(hash) % colors.length];
}

function orgTypeBadge(type: string | null | undefined) {
  if (!type) return null;
  const map: Record<string, { label: string; className: string }> = {
    client: { label: "Client", className: "border-blue-300 text-blue-700" },
    revendeur: { label: "Revendeur", className: "border-amber-300 text-amber-700" },
    prospect: { label: "Prospect", className: "border-purple-300 text-purple-700" },
    fournisseur: { label: "Fournisseur", className: "border-green-300 text-green-700" },
    partenaire: { label: "Partenaire", className: "border-teal-300 text-teal-700" },
    autre: { label: "Autre", className: "border-gray-300 text-gray-700" },
  };
  const cfg = map[type];
  if (!cfg) return null;
  return (
    <Badge variant="outline" className={`text-[10px] h-4 px-1 shrink-0 ${cfg.className}`}>
      {cfg.label}
    </Badge>
  );
}

export function CreateContactModal({
  open,
  onClose,
  onCreated,
  initialEmail,
  initialValues,
  uncertainFields = [],
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (contact: Contact) => void;
  initialEmail?: string;
  initialValues?: Partial<{
    prenom: string;
    nom: string;
    poste: string;
    email: string;
    telephone: string;
    telephones: Array<{ libelle: string; numero: string; principal: boolean }>;
    organisationType: string;
    organisationLibre: string;
    organisationId: string | null;
    notes: string;
    linkedin: string;
    source: string;
    extractionReceipt: string;
  }>;
  uncertainFields?: string[];
}) {
  const { toast } = useToast();
  const [form, setForm] = useState({
    prenom: "",
    nom: "",
    poste: "",
    email: initialEmail || "",
    telephone: "",
    organisationType: "",
    organisationLibre: "",
    notes: "",
    linkedin: "",
  });
  const [emailDuplicate, setEmailDuplicate] = useState<Contact | null>(null);
  const [extractedTelephones, setExtractedTelephones] = useState<Array<{ libelle: string; numero: string; principal: boolean }>>([]);
  const [checkingEmail, setCheckingEmail] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [lienEnabled, setLienEnabled] = useState(false);
  const [lienData, setLienData] = useState<LienEntiteFormData>({
    entiteType: "client", entiteId: null, categorie: "intervenant_exterieur", precisionLien: "", missionId: null,
  });

  useEffect(() => {
    if (open) {
      setForm({
        prenom: initialValues?.prenom || "",
        nom: initialValues?.nom || "",
        poste: initialValues?.poste || "",
        email: initialValues?.email || initialEmail || "",
        telephone: initialValues?.telephone || "",
        organisationType: initialValues?.organisationType || "",
        organisationLibre: initialValues?.organisationLibre || "",
        notes: initialValues?.notes || "",
        linkedin: initialValues?.linkedin || "",
      });
      setEmailDuplicate(null);
      setExtractedTelephones(initialValues?.telephones || []);
      setLienEnabled(false);
      setLienData({ entiteType: "client", entiteId: null, categorie: "intervenant_exterieur", precisionLien: "", missionId: null });
    }
  }, [open, initialEmail, initialValues]);

  const checkEmail = useCallback((email: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!email || !email.includes("@")) { setEmailDuplicate(null); return; }
    debounceRef.current = setTimeout(async () => {
      setCheckingEmail(true);
      try {
        const res = await authFetch(`/api/contacts/check-email?email=${encodeURIComponent(email)}`);
        if (res.ok) {
          const data = await res.json();
          setEmailDuplicate(data.existing || null);
        }
      } catch { /* silent */ } finally { setCheckingEmail(false); }
    }, 500);
  }, []);

  const handleEmailChange = (v: string) => {
    setForm(prev => ({ ...prev, email: v }));
    checkEmail(v);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.prenom.trim() || !form.nom.trim()) {
      toast({ title: "Prénom et nom requis", variant: "destructive" });
      return;
    }
    try {
      const telephonePrincipal = form.telephone.trim();
      const telephones = extractedTelephones.length
        ? extractedTelephones.filter(telephone => telephone.numero.trim())
        : telephonePrincipal
          ? [{ libelle: "Mobile", numero: telephonePrincipal, principal: true }]
          : [];
      const body: any = {
        prenom: form.prenom.trim(),
        nom: form.nom.trim(),
        poste: form.poste.trim() || null,
        email: form.email.trim().toLowerCase() || null,
        telephones,
        organisationType: form.organisationType || null,
        organisationId: initialValues?.organisationId || null,
        organisationLibre: form.organisationLibre.trim() || null,
        notes: form.notes.trim() || null,
        linkedinUrl: form.linkedin.trim() || null,
        source: initialValues?.source || "manuel",
        extractionReceipt: initialValues?.extractionReceipt,
      };
      const res = await authFetch("/api/contacts", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
      if (!res.ok) throw new Error(await res.text());
      const created = await res.json();
      if (lienEnabled && lienData.entiteId) {
        try {
          const lienRes = await authFetch("/api/contact-liens", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              contact_id: created.id,
              entite_type: lienData.entiteType,
              entite_id: lienData.entiteId,
              categorie: lienData.categorie,
              precision_lien: lienData.precisionLien || null,
              mission_id: lienData.missionId || null,
            }),
          });
          if (!lienRes.ok && lienRes.status !== 409) {
            toast({ title: "Contact créé", description: "Le lien d'intervention n'a pas pu être enregistré. Réessayez depuis la fiche.", variant: "destructive" });
          }
        } catch {
          toast({ title: "Contact créé", description: "Le lien n'a pas pu être enregistré. Réessayez depuis la fiche." });
        }
      }
      onCreated(created);
      onClose();
      toast({ title: "Contact créé" });
    } catch (err: any) {
      toast({ title: "Erreur", description: err.message, variant: "destructive" });
    }
  };

  if (!open) return null;
  const isUncertain = (...fieldNames: string[]) =>
    uncertainFields.some(field => fieldNames.includes(field.trim().toLowerCase()));
  const uncertainClass = "border-amber-400 bg-amber-50/70 dark:bg-amber-950/20";
  const VerificationLabel = () => (
    <span className="ml-1 text-[10px] font-medium text-amber-700 dark:text-amber-400">À vérifier</span>
  );
  return (
    <Dialog open={open} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>Nouveau contact</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 pt-1">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Prénom *{isUncertain("prenom", "prénom") && <VerificationLabel />}</Label>
              <Input className={isUncertain("prenom", "prénom") ? uncertainClass : undefined} value={form.prenom} onChange={e => setForm(prev => ({ ...prev, prenom: e.target.value }))} placeholder="Jean" data-testid="input-contact-prenom" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Nom *{isUncertain("nom") && <VerificationLabel />}</Label>
              <Input className={isUncertain("nom") ? uncertainClass : undefined} value={form.nom} onChange={e => setForm(prev => ({ ...prev, nom: e.target.value }))} placeholder="Dupont" data-testid="input-contact-nom" />
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Poste{isUncertain("poste", "fonction") && <VerificationLabel />}</Label>
            <Input className={isUncertain("poste", "fonction") ? uncertainClass : undefined} value={form.poste} onChange={e => setForm(prev => ({ ...prev, poste: e.target.value }))} placeholder="Directeur technique" data-testid="input-contact-poste" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Email{isUncertain("email") && <VerificationLabel />}</Label>
            <Input
              className={isUncertain("email") ? uncertainClass : undefined}
              type="email"
              value={form.email}
              onChange={e => handleEmailChange(e.target.value)}
              placeholder="jean.dupont@exemple.fr"
              data-testid="input-contact-email"
            />
            {checkingEmail && <p className="text-xs text-muted-foreground">Vérification...</p>}
            {emailDuplicate && (
              <div className="rounded-md border border-amber-200 bg-amber-50 dark:bg-amber-950/20 p-2 text-xs">
                <p className="text-amber-800 dark:text-amber-400 font-medium">
                  Ce contact existe déjà : {emailDuplicate.prenom} {emailDuplicate.nom}
                </p>
                <button
                  type="button"
                  className="text-primary hover:underline mt-1"
                  onClick={() => { onCreated(emailDuplicate); onClose(); }}
                >
                  Utiliser ce contact →
                </button>
              </div>
            )}
          </div>
          {extractedTelephones.length > 0 ? (
            <div className="space-y-2">
              <Label className="text-xs">Téléphones extraits{isUncertain("telephone", "téléphone", "telephones", "téléphones") && <VerificationLabel />}</Label>
              {extractedTelephones.map((telephone, index) => (
                <div key={index} className="grid grid-cols-[90px_1fr] gap-2">
                  <div className="flex items-center rounded-md border bg-muted/30 px-3 text-xs capitalize">
                    {telephone.libelle}{telephone.principal ? " · principal" : ""}
                  </div>
                  <Input
                    className={isUncertain("telephone", "téléphone", "telephones", "téléphones") ? uncertainClass : undefined}
                    value={telephone.numero}
                    onChange={event => setExtractedTelephones(current => current.map((item, itemIndex) =>
                      itemIndex === index ? { ...item, numero: event.target.value } : item
                    ))}
                    data-testid={`input-contact-telephone-${index}`}
                  />
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-1">
              <Label className="text-xs">Téléphone principal{isUncertain("telephone", "téléphone", "telephones", "téléphones") && <VerificationLabel />}</Label>
              <Input className={isUncertain("telephone", "téléphone", "telephones", "téléphones") ? uncertainClass : undefined} value={form.telephone} onChange={e => setForm(prev => ({ ...prev, telephone: e.target.value }))} placeholder="+33 6 12 34 56 78" data-testid="input-contact-telephone" />
            </div>
          )}
          <div className="space-y-1">
            <Label className="text-xs">Organisation</Label>
            <div className="flex gap-2">
              <Select value={form.organisationType} onValueChange={v => setForm(prev => ({ ...prev, organisationType: v }))}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="client">Client</SelectItem>
                  <SelectItem value="revendeur">Revendeur</SelectItem>
                  <SelectItem value="prospect">Prospect</SelectItem>
                  <SelectItem value="fournisseur">Fournisseur</SelectItem>
                  <SelectItem value="partenaire">Partenaire</SelectItem>
                  <SelectItem value="autre">Autre</SelectItem>
                </SelectContent>
              </Select>
              <Input
                value={form.organisationLibre}
                onChange={e => setForm(prev => ({ ...prev, organisationLibre: e.target.value }))}
                placeholder="Nom de l'organisation"
                className="flex-1"
                data-testid="input-contact-org"
              />
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Profil LinkedIn (optionnel)</Label>
            <Input value={form.linkedin} onChange={e => setForm(prev => ({ ...prev, linkedin: e.target.value }))} placeholder="linkedin.com/in/jean-dupont" data-testid="input-contact-linkedin" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Notes</Label>
            <Input value={form.notes} onChange={e => setForm(prev => ({ ...prev, notes: e.target.value }))} placeholder="Informations complémentaires..." data-testid="input-contact-notes" />
          </div>
          <LienEntiteForm
            value={lienData}
            onChange={setLienData}
            enabled={lienEnabled}
            onToggle={() => setLienEnabled(v => !v)}
          />
          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="outline" onClick={onClose}>Annuler</Button>
            <Button type="submit" disabled={Boolean(emailDuplicate)} data-testid="button-create-contact-submit">Créer le contact</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function ContactSelector({
  value,
  onChange,
  multiple = false,
  organisationFilter,
  allowCreate = true,
  placeholder = "Rechercher un contact ou une société...",
  disabled = false,
}: ContactSelectorProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedSearch(search), 200);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [search]);

  const { data: searchResults = [], isLoading } = useQuery<Contact[]>({
    queryKey: ["/api/contacts", debouncedSearch, organisationFilter],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (debouncedSearch) params.set("q", debouncedSearch);
      if (organisationFilter) params.set("organisationId", organisationFilter);
      params.set("limit", "30");
      const res = await authFetch(`/api/contacts?${params.toString()}`);
      if (!res.ok) return [];
      return res.json();
    },
    enabled: open,
  });

  const { data: selectedContacts = [] } = useQuery<Contact[]>({
    queryKey: ["/api/contacts/batch", value.join(",")],
    queryFn: async () => {
      if (!value.length) return [];
      const res = await authFetch(`/api/contacts/batch?ids=${value.join(",")}`);
      if (!res.ok) return [];
      return res.json();
    },
    enabled: value.length > 0,
  });

  const toggleContact = (contact: Contact) => {
    if (multiple) {
      if (value.includes(contact.id)) {
        onChange(value.filter(id => id !== contact.id));
      } else {
        onChange([...value, contact.id]);
      }
    } else {
      onChange(value.includes(contact.id) ? [] : [contact.id]);
      setOpen(false);
    }
  };

  const removeContact = (id: string) => onChange(value.filter(v => v !== id));

  const grouped = searchResults.reduce((acc: Record<string, Contact[]>, c) => {
    const key = c.organisationNom || c.organisationLibre || c.organisationType || "Autre";
    (acc[key] = acc[key] || []).push(c);
    return acc;
  }, {});

  return (
    <div className="space-y-2" data-testid="contact-selector">
      {selectedContacts.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {selectedContacts.map(c => (
            <span
              key={c.id}
              className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium"
              data-testid={`contact-tag-${c.id}`}
            >
              <Avatar className="h-4 w-4">
                {c.avatarUrl && <AvatarImage src={c.avatarUrl} />}
                <AvatarFallback className={`text-[8px] text-white ${avatarColor(c.prenom + c.nom)}`}>
                  {avatarInitials(c.prenom, c.nom)}
                </AvatarFallback>
              </Avatar>
              {c.prenom} {c.nom}
              {!disabled && (
                <button
                  type="button"
                  onClick={() => removeContact(c.id)}
                  className="hover:text-destructive transition-colors"
                  data-testid={`btn-remove-contact-${c.id}`}
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </span>
          ))}
        </div>
      )}

      {!disabled && (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              className="w-full justify-start text-muted-foreground font-normal h-9"
              data-testid="btn-contact-selector-open"
            >
              <Search className="h-3.5 w-3.5 mr-2 shrink-0" />
              <span className="truncate">{placeholder}</span>
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[380px] p-0" align="start">
            <div className="flex items-center border-b px-3 py-2">
              <Search className="h-4 w-4 text-muted-foreground mr-2 shrink-0" />
              <input
                className="flex-1 text-sm bg-transparent outline-none placeholder:text-muted-foreground"
                placeholder="Prénom, nom, société, email..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                autoFocus
                data-testid="input-contact-search"
              />
              {search && (
                <button onClick={() => setSearch("")} className="text-muted-foreground hover:text-foreground">
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>

            <div className="max-h-64 overflow-y-auto">
              {isLoading ? (
                <p className="text-xs text-muted-foreground text-center py-4">Recherche...</p>
              ) : searchResults.length === 0 ? (
                <p className="text-xs text-muted-foreground text-center py-4">Aucun résultat</p>
              ) : (
                Object.entries(grouped).map(([org, contacts]) => (
                  <div key={org}>
                    <div className="px-3 py-1.5 text-[10px] font-semibold text-muted-foreground uppercase tracking-wide bg-muted/40">
                      {org}
                    </div>
                    {contacts.map(contact => {
                      const selected = value.includes(contact.id);
                      const principal = contact.telephones?.find(t => t.principal) || contact.telephones?.[0];
                      return (
                        <button
                          key={contact.id}
                          type="button"
                          className={`w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-muted/50 transition-colors ${selected ? "bg-primary/5" : ""}`}
                          onClick={() => toggleContact(contact)}
                          data-testid={`contact-option-${contact.id}`}
                        >
                          <Avatar className="h-7 w-7 shrink-0">
                            {contact.avatarUrl && <AvatarImage src={contact.avatarUrl} />}
                            <AvatarFallback className={`text-[10px] text-white ${avatarColor(contact.prenom + contact.nom)}`}>
                              {avatarInitials(contact.prenom, contact.nom)}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5">
                              <span className="text-sm font-medium truncate">{contact.prenom} {contact.nom}</span>
                              {orgTypeBadge(contact.organisationType)}
                              {selected && <span className="ml-auto text-primary text-xs shrink-0">✓</span>}
                            </div>
                            {contact.poste && <p className="text-xs text-muted-foreground truncate">{contact.poste}</p>}
                            {principal && (
                              <p className="text-xs text-muted-foreground flex items-center gap-1">
                                <Phone className="h-2.5 w-2.5" />
                                {principal.numero}
                              </p>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                ))
              )}
            </div>

            {allowCreate && (
              <div className="border-t p-2">
                <button
                  type="button"
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-primary hover:bg-primary/5 rounded-md transition-colors"
                  onClick={() => { setOpen(false); setCreateOpen(true); }}
                  data-testid="btn-create-new-contact"
                >
                  <Plus className="h-4 w-4" />
                  Créer un nouveau contact
                  {search && <span className="text-muted-foreground">"{search}"</span>}
                </button>
              </div>
            )}
          </PopoverContent>
        </Popover>
      )}

      <CreateContactModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        initialEmail={search.includes("@") ? search : undefined}
        onCreated={contact => {
          onChange(multiple ? [...value, contact.id] : [contact.id]);
        }}
      />
    </div>
  );
}
